// src/components/Inputs/SalaryCalculator/PensionInput.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Link,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { isFeatureEnabled } from '../../../../featureFlags';

const PensionInput = ({
  pensionContribution,
  pensionInputType,
  pensionScheme,
  pensionCalculationBasis,
  onPensionContributionChange,
  onPensionInputTypeChange,
  onPensionSchemeChange,
  onPensionCalculationBasisChange,
  salary,  // user's salary
  includeOvertime, // whether overtime is included
  onIncludeOvertimeChange, // callback to pass overtime toggle change to parent
  enable2029Mode, // 2029 Mode state
  onEnable2029ModeChange, // callback for 2029 Mode toggle
  taxYear // current tax year selection
}) => {
  // The maximum pension contribution is the lesser of the user's salary or £60,000.
  const maxContribution = Math.min(salary, 60000);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for controlling 2029 mode info display
  const [infoOpen, setInfoOpen] = useState(false);

  const handleInfoToggle = () => {
    setInfoOpen(!infoOpen);
  };

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  // When the scheme is Personal, force the input type to "amount"
  useEffect(() => {
    if (pensionScheme === 'Personal' && pensionInputType !== 'amount') {
      onPensionInputTypeChange('amount');
    }
  }, [pensionScheme, pensionInputType, onPensionInputTypeChange]);

  const handleContributionChange = (event) => {
    const input = event.target.value;
    // If the field is cleared, treat it as zero.
    if (input === '') {
      onPensionContributionChange(0);
    } else {
      let value = Number(input);
      // Clamp the value so it does not exceed maxContribution.
      if (value > maxContribution) {
        value = maxContribution;
      }
      onPensionContributionChange(value);
    }
  };

  const handleSchemeChange = (event) => {
    onPensionSchemeChange(event.target.value);
  };

  const handleCalculationBasisChange = (event) => {
    onPensionCalculationBasisChange(event.target.value);
  };

  const handleToggleChange = (event, newType) => {
    if (newType !== null) {
      onPensionInputTypeChange(newType);
    }
  };

  // When the scheme is Personal, force the label to show pounds.
  const fieldLabel =
    pensionScheme === 'Personal'
      ? 'Pension Contribution (£)'
      : pensionInputType === 'amount'
      ? 'Pension Contribution (£)'
      : 'Pension Contribution (%)';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Pension
      </Typography>
      {/* Scheme selector */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="pension-scheme-label">Pension Scheme</InputLabel>
        <Select
          labelId="pension-scheme-label"
          value={pensionScheme}
          label="Pension Scheme"
          onChange={handleSchemeChange}
        >
          <MenuItem value="Auto-enrolment">Auto-enrolment</MenuItem>
          <MenuItem value="Employer/Occupational">Employer/Occupational</MenuItem>
          <MenuItem value="Salary Sacrifice">Salary Sacrifice</MenuItem>
          <MenuItem value="Personal">Personal</MenuItem>
        </Select>
      </FormControl>
      {/* Calculation basis (only if not Personal) */}
      {pensionScheme !== 'Personal' && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="pension-calculation-label">Pension Calculation Basis</InputLabel>
          <Select
            labelId="pension-calculation-label"
            value={pensionCalculationBasis}
            label="Pension Calculation Basis"
            onChange={handleCalculationBasisChange}
          >
            <MenuItem value="Qualifying Earnings">Qualifying Earnings</MenuItem>
            <MenuItem value="Gross Salary">Gross Salary</MenuItem>
          </Select>
        </FormControl>
      )}
      {/* Contribution type controls */}
      <ToggleButtonGroup
        value={pensionInputType}
        exclusive
        onChange={handleToggleChange}
        sx={{ mb: 2 }}
      >
        {pensionScheme === 'Personal'
          ? <ToggleButton value="amount">£</ToggleButton>
          : [
              <ToggleButton key="percentage" value="percentage">%</ToggleButton>,
              <ToggleButton key="amount" value="amount">£</ToggleButton>
            ]
        }
      </ToggleButtonGroup>
      <TextField
        label={fieldLabel}
        type="number"
        // When pensionContribution is 0, show an empty field to allow easy editing.
        value={pensionContribution === 0 ? '' : pensionContribution}
        onChange={handleContributionChange}
        variant="outlined"
        fullWidth
        inputProps={{ min: 0, max: maxContribution }}
        sx={{ mb: 2 }}
      />
      {/* Include Overtime Toggle (only if scheme is not Personal) */}
      {pensionScheme !== 'Personal' && (
        <FormControlLabel
          control={
            <Switch
              checked={includeOvertime}
              onChange={(e) => onIncludeOvertimeChange(e.target.checked)}
              name="includeOvertime"
              color="primary"
            />
          }
          label="Include Overtime"
          sx={{ mb: 2 }}
        />
      )}
      {/* 2029 Mode Toggle (only for Salary Sacrifice, tax year 2025+, and when feature enabled) */}
      {pensionScheme === 'Salary Sacrifice' &&
       isFeatureEnabled('PENSION_2029_MODE') &&
       parseInt(taxYear) >= 2025 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enable2029Mode}
                onChange={(e) => onEnable2029ModeChange(e.target.checked)}
                name="enable2029Mode"
                color="primary"
              />
            }
            label="2029 Mode"
          />

          {/* Mobile: Dialog, Desktop: Click-controlled Tooltip */}
          {isMobile ? (
            <>
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={handleInfoToggle}
                aria-label="Information about 2029 Mode"
              >
                <InfoOutlinedIcon fontSize="small" color="primary" />
              </IconButton>
              <Dialog
                open={infoOpen}
                onClose={handleInfoClose}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  What is 2029 Mode?
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleInfoClose}
                    aria-label="close"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    From 2029, the UK Government has announced that salary sacrifice pension
                    contributions above £2,000 per year will become subject to National Insurance.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Our Interpretation:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    We treat the chargeable portion (contributions above £2,000) as additional
                    NI-able income, passing it through the standard NI banding system (8% and 2%)
                    based on your post-sacrifice earnings. This ensures fair, progressive taxation
                    and avoids regressive outcomes for lower earners.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    How to read the Summary Table:
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    • <strong>National Insurance</strong> - your standard NI (on salary after sacrifice)
                    <br />
                    • <strong>2029 NI Surcharge</strong> - additional NI on contributions above £2,000
                    <br />
                    • Your take-home pay is reduced by the surcharge amount
                  </Typography>
                  <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                    Note: This is our interpretation of the announcement. Final implementation details
                    may differ when the policy is enacted.
                  </Typography>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    What is 2029 Mode?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    From 2029, the UK Government has announced that salary sacrifice pension
                    contributions above £2,000 per year will become subject to National Insurance.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Our Interpretation:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    We treat the chargeable portion (contributions above £2,000) as additional
                    NI-able income, passing it through the standard NI banding system (8% and 2%)
                    based on your post-sacrifice earnings. This ensures fair, progressive taxation
                    and avoids regressive outcomes for lower earners.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    How to read the Summary Table:
                  </Typography>
                  <Typography variant="body2" component="div">
                    • <strong>National Insurance</strong> - your standard NI (on salary after sacrifice)
                    <br />
                    • <strong>2029 NI Surcharge</strong> - additional NI on contributions above £2,000
                    <br />
                    • Your take-home pay is reduced by the surcharge amount
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                    Note: This is our interpretation of the announcement. Final implementation details
                    may differ when the policy is enacted.
                  </Typography>
                </Box>
              }
              arrow
              placement="right"
              open={infoOpen}
              onClose={handleInfoClose}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={handleInfoToggle}
                aria-label="Information about 2029 Mode"
              >
                <InfoOutlinedIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
        Note: This calculator is configured so that the maximum allowable pension contribution is the lesser of your salary or £60,000.
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
        <Link component={RouterLink} to="/about-the-salary-calculator#pension-contributions" color="primary" underline="hover">
          Learn more about how pension contributions affect your take-home pay.
        </Link>
      </Typography>
    </Box>
  );
};

export default PensionInput;
